import {
    defineComponent,
    reactive,
    ref,
    toRaw,
    watch,
    PropType,
    VNodeChild,
    onMounted
} from "vue";

import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
    useFetch
} from "../../utils.js";

import ImageFallback from "../imageFallback.js";

type FilterValue = {
    id              : string;
    [key: string]   : any;
};

export const FilterBaseProps = {
    filter: {
        type        : Object as PropType<any>,
        required    : true,
    },

    values: {
        type        : Object as PropType<any>,
        required    : true,
    },

    onselect: {
        type        : Function as PropType<(values: any) => void>,
        required    : true,
    }
} as const;

export const FilterBase = defineComponent({

    props: {
        ...FilterBaseProps,

        renderOptionLabel: {
            type        : Function as PropType<(value: FilterValue) => VNodeChild>,
            required    : true,
        },

        onSearch: {
            type        : Function as PropType<(query: string) => FilterValue[] | Promise<FilterValue[]>>,
            required    : true,
        },
    },

    setup(props) {
        const refSearch     = ref("");
        const refOptions    = ref<any[]>([]);
        const refLoading    = ref(false);
        const values        = props.values
            ? reactive<Map<string, FilterValue>>(structuredClone(toRaw(props.values)) as any)
            : reactive<Map<string, FilterValue>>(new Map());

        function select(object: any) {
            const id = props.filter.selectOption(object);

            values.has(id)
                ? values.delete(id)
                : values.set(id, object);
        }

        function onInput(e: Event) {
            refSearch.value = (e.target as HTMLInputElement).value;
            onSearch();
        }

        async function onSearch() {
            if (refLoading.value)
                return;

            try {
                refLoading.value = true;

                while (true) {
                    const search = refSearch.value;
                    const options = await props.onSearch(search);

                    // Set options.
                    refOptions.value = options;

                    if (search === refSearch.value)
                        return;
                }

            } finally {
                refLoading.value = false;
            }
        }

        function renderOptions() {
            if (refLoading.value)
                return <div class="log-filter-infos">Recherche en cours...</div>;

            if (refOptions.value.length === 0)
                return <div class="log-filter-infos">Aucun résultat.</div>;

            return refOptions.value.map((option) => (
                <div class="log-filter-option" onClick={() => select(option)}>
                    <div class="log-filter-option-selected">
                        {values.has(props.filter.selectOption(option)) && <span class="material-symbols-outlined">check</span>}
                    </div>

                    {props.renderOptionLabel(option)}
                </div>
            ));
        }

        onMounted(onSearch);

        return () => (
            <>
                <div class="filter-search-wrapper">
                    <div class="material-symbols-outlined">
                        search
                    </div>

                    <input class="filter-search-input" type="text" placeholder="Rechercher une option" onInput={onInput} />
                </div>

                <div class="filter-window">
                    <div class="filter-options">
                        {renderOptions()}
                    </div>

                    <div class="filter-results">
                        <div class="filter-results-name">
                            <b>{values.size}</b> options séléctionnées
                        </div>

                        {[...values.values()].map((option) => (
                            <div class="log-filter-option" onClick={() => select(option)}>
                                <div class="log-filter-option-selected">
                                    {props.filter.selectOption(option) && <span class="material-symbols-outlined">check</span>}
                                </div>

                                {props.renderOptionLabel(option)}
                            </div>
                        ))}
                    </div>
                </div>

                <div class="filter-submit" onClick={() => props.onselect(values)}>
                    <span class="material-symbols-outlined">save</span>
                    Enregistrer le filtre
                </div>
            </>
        );
    },
});

export const FilterUsers = defineComponent({
    props: FilterBaseProps,

    setup(props) {
        const { fetch } = useFetch();

        async function onSearch(query: string) {
            const res = await fetch(`/api/search-users?q=${encodeURIComponent(query)}`);

            if (res.status === 200) {
                const users = await res.json();

                return users;

            } else {
                return [];
            }
        }

        function renderOptionLabel(member: FilterValue) {
            return (
                <div class="log-filter-option-label">
                    <ImageFallback class="log-filter-member-avatar" src={getMemberAvatar(member as any)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(member as any)} />
                    <span class="log-filter-member-displayname">{getMemberDisplayName(member as any)}</span>

                    <div class="log-filter-member-username">
                        {getMemberTag(member as any)}
                    </div>
                </div>
            );
        }

        return () => <FilterBase {...props}
            renderOptionLabel={renderOptionLabel}
            onSearch={onSearch} />
    },
});

export const FilterStatic = defineComponent({
    props: FilterBaseProps,

    setup(props) {
        const refOptions = ref([]);
        const refLoading = ref(false);

        function onSearch(query: string) {
            return props.filter.searchOption(refOptions.value, query.toLowerCase());
        }

        function renderOptionLabel(option: FilterValue) {
            return (
                <div class="log-filter-option-label">
                    {props.filter.renderOption(option)}
                </div>
            );
        }

        watch(() => props.filter.options, () => {
            const isFunc = typeof props.filter.options === "function";

            if (isFunc) {
                const result = props.filter.options();

                if (typeof result.then === "function" && typeof result.finally === "function") {
                    refLoading.value = true;

                    result
                        .then((value: any)  => (refOptions.value = value))
                        .finally(()         => (refLoading.value = false));

                } else {
                    refOptions.value = result;
                }

            } else {
                refOptions.value = props.filter.options;
            }
        }, { immediate: true });

        return () => {
            if (refLoading.value)
                return <div class="log-filter-infos">Chargement des options...</div>;

            return <FilterBase {...props}
                renderOptionLabel={renderOptionLabel}
                onSearch={onSearch} />;
        };
    },
});
